// src/utils/attribution.js
//
// Passive signup attribution. Fewer than half of new groomers answer "How did
// you hear about us?", so we also remember how each browser first arrived:
// the page it landed on, the site that sent it, and any campaign tags.
//
// First touch is written once and never overwritten. UTM tags seen on any
// later page still update the legacy `utm_source` / `utm_campaign` keys that
// Signup.js has always read, so a tagged link to the homepage is credited even
// though the visitor signs up three pages later.

const FIRST_TOUCH_KEY = "groomsy_first_touch";
const MAX_LEN = 120;

const clean = (v) => (v ? String(v).trim().slice(0, MAX_LEN) : null);

export function referrerHost(referrer, ownHost) {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, "");
    const own = String(ownHost || "").toLowerCase().replace(/^www\./, "");
    if (!host || (own && (host === own || host.endsWith(`.${own}`)))) return null;
    return clean(host);
  } catch {
    return null;
  }
}

export function buildFirstTouch({ search = "", pathname = "/", referrer = "", ownHost = "", now = new Date() } = {}) {
  const params = new URLSearchParams(search);
  const clickId = params.get("gclid")
    ? "gclid"
    : params.get("fbclid")
      ? "fbclid"
      : params.get("msclkid")
        ? "msclkid"
        : null;
  return {
    landingPath: clean(pathname) || "/",
    referrerHost: referrerHost(referrer, ownHost),
    utmSource: clean(params.get("utm_source")),
    utmMedium: clean(params.get("utm_medium")),
    utmCampaign: clean(params.get("utm_campaign")),
    utmTerm: clean(params.get("utm_term")),
    clickId,
    firstSeenAt: now.toISOString(),
  };
}

// Call once when the app boots.
export function captureFirstTouch() {
  try {
    const { search, pathname, hostname } = window.location;
    const params = new URLSearchParams(search);
    const utmSource = params.get("utm_source");
    const utmCampaign = params.get("utm_campaign");
    if (utmSource) localStorage.setItem("utm_source", utmSource);
    if (utmCampaign) localStorage.setItem("utm_campaign", utmCampaign);

    if (localStorage.getItem(FIRST_TOUCH_KEY)) return;
    localStorage.setItem(
      FIRST_TOUCH_KEY,
      JSON.stringify(
        buildFirstTouch({ search, pathname, referrer: document.referrer, ownHost: hostname }),
      ),
    );
  } catch {
    // Storage blocked: attribution is best-effort.
  }
}

export function getFirstTouch() {
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}
